/**
 * The gesture unlock and every subsequent `play()` (spec.md §13.2).
 *
 * ## Why this is its own module
 *
 * There is exactly one moment in the app's lifetime that cannot be retried: the first
 * `play()`. WebKit's normative rule is that the script calling `play()` "must have directly
 * resulted from a handler for a `touchend`, `click`, `dblclick` or `keydown` event". A
 * `play()` issued after `await fetch(...)` is a **different task** and no longer counts as
 * being inside the gesture — the element stays locked and every later `play()` rejects with
 * `NotAllowedError`, including the ones that come from the queue advancing on its own.
 *
 * {@link unlockWith} therefore does its work in a strict order, with **no `await` before
 * `play()`**:
 *
 * ```
 * el.src = src            →  el.currentTime = startAtMs/1000  →  el.play()
 * ```
 *
 * ...and only then, in an async continuation, records the unlock and raises `preload`.
 * Any reordering, any early `await`, and any "load the URL first, then play" refactor
 * breaks resume-from-position on iOS silently. `unlock.test.ts` asserts the order rather
 * than the outcome for exactly that reason.
 *
 * ## Why `currentTime` is written before metadata exists
 *
 * Writing `currentTime` while `readyState === HAVE_NOTHING` is not a seek. The HTML media
 * spec routes it into the element's **default playback start position**, which the resource
 * selection algorithm then applies as soon as the media is loaded. That is the only way to
 * resume mid-track without a visible jump from 0 and without a `loadedmetadata` listener
 * that would have to run *outside* the gesture. It also means no double seek and no
 * flash of `0:00` in the UI.
 *
 * ## Platform notes (from client source, spec.md §13.2)
 *
 * - **iOS**: Telegram's `WKWebView` sets `mediaTypesRequiringUserActionForPlayback = []`,
 *   so a gesture-free `play()` would in fact work — but we never rely on it, because the
 *   same code runs on Android.
 * - **Android**: setup calls `setMediaPlaybackRequiresUserGesture(false)`, then
 *   `onPageStarted` sets it **back to true**, and only the first `ACTION_DOWN` anywhere in
 *   the webview clears it again. So: never attempt autoplay on boot, always render a play
 *   button, and expect a mid-session reload to re-lock auto-advance until the next tap.
 */

import { audio, type MediaElementLike } from './element';

/* ══ outcomes ══════════════════════════════════════════════════════════════════════ */

/**
 * Why a `play()` did not start playback.
 *
 * - `gesture-required` — `NotAllowedError`. The element is locked; show the play button.
 *   This is the **only** reason the user must be told about.
 * - `unsupported` — `NotSupportedError`. `MEDIA_ERR_SRC_NOT_SUPPORTED` latches on the
 *   element and stays latched until the resource selection algorithm runs again, so a bare
 *   retry can never clear it: `load()` has to run first.
 * - `aborted` — `AbortError`. A `pause()` or a `load()` raced the pending `play()`, which
 *   is what a fast skip looks like. Not an error; never surfaced.
 * - `unknown` — anything else. Treated as retryable by `recovery.ts`.
 */
export type PlayFailureReason = 'gesture-required' | 'unsupported' | 'aborted' | 'unknown';

export type PlayOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: PlayFailureReason; readonly error: unknown };

const STARTED: PlayOutcome = { ok: true };

/* ══ the unlocked flag ═════════════════════════════════════════════════════════════ */

let unlockedFlag = false;

/**
 * `true` once a gesture-driven `play()` on the singleton element has resolved.
 *
 * The UI uses this to decide whether auto-advance may be attempted at all: before the
 * unlock, the end of a track must surface a play button instead of silently failing.
 */
export function isUnlocked(): boolean {
  return unlockedFlag;
}

/**
 * Forgets the unlock.
 *
 * Real use: a mid-session page reload re-locks the element on Android, and the shell calls
 * this when it detects one. Tests use it to isolate cases.
 */
export function resetUnlocked(): void {
  unlockedFlag = false;
}

/* ══ rejection triage ══════════════════════════════════════════════════════════════ */

/** `error.name` for anything, including the non-`Error` values some webviews reject with. */
function errorName(error: unknown): string {
  if (error instanceof Error) return error.name;
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const name: unknown = error.name;
    if (typeof name === 'string') return name;
  }
  return '';
}

/** Maps a `play()` rejection onto a {@link PlayFailureReason}. */
export function classifyPlayRejection(error: unknown): PlayFailureReason {
  switch (errorName(error)) {
    case 'NotAllowedError':
      return 'gesture-required';
    case 'NotSupportedError':
      return 'unsupported';
    case 'AbortError':
      return 'aborted';
    default:
      return 'unknown';
  }
}

function failed(error: unknown): PlayOutcome {
  return { ok: false, reason: classifyPlayRejection(error), error };
}

/**
 * Awaits a `play()` promise and converts it into an outcome.
 *
 * Tolerates a legacy engine that returns `undefined` instead of a promise — `await
 * undefined` simply resolves — so the caller never has to branch on it.
 */
async function settle(started: Promise<void>): Promise<PlayOutcome> {
  try {
    await started;
    return STARTED;
  } catch (error) {
    return failed(error);
  }
}

/** Invokes `play()`, converting a *synchronous* throw into the same outcome shape. */
function attemptPlay(el: MediaElementLike): Promise<PlayOutcome> {
  try {
    return settle(el.play());
  } catch (error) {
    return Promise.resolve(failed(error));
  }
}

/* ══ unlockWith ════════════════════════════════════════════════════════════════════ */

/**
 * Points the element at `src`, optionally at `startAtMs`, and starts playback — all of it
 * synchronously, so the call may (and must) be made from inside a tap handler.
 *
 * This is **not** an `async function` on purpose. An `async function` would still run its
 * prologue synchronously, but the guarantee would then depend on nobody ever inserting an
 * `await` above the `play()` call; a plain function that returns a promise makes the
 * invariant structural.
 *
 * @param src       the media URL — already minted, never fetched here
 * @param startAtMs resume position in milliseconds; `0` starts at the beginning
 * @param el        the element to drive; defaults to the app singleton
 */
export function unlockWith(
  src: string,
  startAtMs = 0,
  el: MediaElementLike = audio,
): Promise<PlayOutcome> {
  /* ── ORDER IS LOAD-BEARING. Do not add an `await` above `el.play()`. ───────────── */
  el.src = src;
  if (startAtMs > 0 && Number.isFinite(startAtMs)) {
    // Written while `readyState === HAVE_NOTHING`: this is the default playback start
    // position, not a seek. See the module header.
    el.currentTime = startAtMs / 1000;
  }
  const started = attemptPlay(el);
  /* ── everything below this line is allowed to be asynchronous ──────────────────── */

  return started.then((outcome) => {
    if (!outcome.ok) return outcome;
    unlockedFlag = true;
    // Permitted on this element now that it is unlocked, iOS included. Before the
    // unlock it would have been a speculative download on a metered connection.
    el.preload = 'metadata';
    return outcome;
  });
}

/* ══ safePlay ══════════════════════════════════════════════════════════════════════ */

/**
 * `play()` with the full §13.2 triage, for every call that is **not** the first one:
 * resuming after a pause, advancing the queue on `ended`, a lock-screen play button, a
 * recovery attempt.
 *
 * - `NotAllowedError` → `onNeedGesture()` **exactly once**, so the UI can fall back to a
 *   play button. Never retried: only a gesture can clear the lock.
 * - `NotSupportedError` → `load()` first (the latched `MEDIA_ERR_SRC_NOT_SUPPORTED` cannot
 *   be cleared any other way), then **one** retry.
 * - `AbortError` → silent. No callback, no throw: the user skipped.
 *
 * Never throws. The outcome is the return value.
 */
export async function safePlay(
  onNeedGesture?: () => void,
  el: MediaElementLike = audio,
): Promise<PlayOutcome> {
  let notified = false;
  const needGesture = (): void => {
    if (notified) return;
    notified = true;
    onNeedGesture?.();
  };

  const first = await attemptPlay(el);
  if (first.ok) {
    unlockedFlag = true;
    return first;
  }

  switch (first.reason) {
    case 'gesture-required':
      needGesture();
      return first;

    case 'aborted':
      return first;

    case 'unsupported': {
      // The load algorithm is the only thing that clears a latched
      // MEDIA_ERR_SRC_NOT_SUPPORTED, so it must run BEFORE the retry.
      try {
        el.load();
      } catch {
        return first;
      }
      const second = await attemptPlay(el);
      if (second.ok) {
        unlockedFlag = true;
        return second;
      }
      if (second.reason === 'gesture-required') needGesture();
      return second;
    }

    case 'unknown':
      return first;

    default:
      return first;
  }
}
